import React, { useState } from 'react';
import { BellSlashIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/solid';
import { BellSlashIcon as BellSlashIconOutline } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from 'react-query';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const NotificationStatusBar = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showNotificationMuteModal, setShowNotificationMuteModal] = useState(false);
  const [isLoadingMuteStatus, setIsLoadingMuteStatus] = useState(false);

  // React Query로 알림 중지 상태 조회
  const { data: notificationMuteStatus } = useQuery(
    ['notification-mute-status'],
    () => api.get('/users/notification-mute/status').then(res => res.data),
    {
      enabled: !!user,
      refetchInterval: 30000, // 30초마다 자동 새로고침
      retry: false
    }
  );

  const handleNotificationMute = async (duration) => {
    try {
      setIsLoadingMuteStatus(true);
      const response = await api.post('/users/notification-mute', { duration });
      toast.success(response.data.message);
      setShowNotificationMuteModal(false);
      // 쿼리 무효화하여 즉시 업데이트
      queryClient.invalidateQueries(['notification-mute-status']);
    } catch (error) {
      toast.error('알림 설정에 실패했습니다.');
    } finally {
      setIsLoadingMuteStatus(false);
    }
  };

  // 알림이 중지된 경우만 표시
  if (!notificationMuteStatus?.is_muted) return null;

  return (
    <>
      <div className="bg-orange-500 text-white">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2">
              <BellSlashIcon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                알림이 일시 중지되었습니다 ({notificationMuteStatus.remaining_minutes}분 남음)
              </span>
            </div>
            <button
              onClick={() => setShowNotificationMuteModal(true)}
              className="text-xs underline hover:no-underline whitespace-nowrap"
            >
              설정 변경
            </button>
          </div>
        </div>
      </div>

      {/* 알림 중지 설정 모달 */}
      {showNotificationMuteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl">
            <div className="p-6">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <BellSlashIconOutline className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                알림 일시 중지
              </h3>
              
              <div className="mb-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-orange-800 mb-1">
                    🔕 현재 알림이 중지된 상태입니다
                  </p>
                  <p className="text-xs text-orange-600">
                    남은 시간: <span className="font-bold">{notificationMuteStatus.remaining_minutes}분</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('알림 중지를 해제하시겠습니까?')) {
                      handleNotificationMute(0);
                    }
                  }}
                  className="w-full px-4 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
                >
                  지금 알림 받기 (중지 해제)
                </button>
              </div>
              
              <div className="space-y-2 mt-4">
                <p className="text-sm text-gray-500 text-center mb-2">
                  중지 시간 연장하기:
                </p>
                {[
                  { duration: 30, label: '30분' },
                  { duration: 60, label: '1시간' },
                  { duration: 90, label: '1시간 30분' },
                  { duration: 120, label: '2시간' }
                ].map((option) => (
                  <button
                    key={option.duration}
                    onClick={() => handleNotificationMute(option.duration)}
                    disabled={isLoadingMuteStatus}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <ClockIcon className="h-5 w-5 text-gray-400 group-hover:text-orange-500" />
                      <span className="font-medium text-gray-700">{option.label} 더 연장</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                * 시스템 내 알림은 정상적으로 표시됩니다.
              </p>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-3xl">
              <button
                onClick={() => setShowNotificationMuteModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationStatusBar;